import { useMemo, useState } from 'react';
import { PickerRow } from './Field.jsx';
import PickerSheet from './PickerSheet.jsx';
import Button from './Button.jsx';
import { db } from '../db/index.js';
import { createDeck, deckPath, flattenTree } from '../db/repo/decks.js';
import styles from './PickerSheet.module.scss';

const NONE = '__none__';

/**
 * Deck chooser: a row showing the chosen deck that opens a sheet of decks.
 * `noneLabel` adds a "no deck" option (e.g. top level); `exclude` hides ids.
 * `allowCreate` lets people make a new deck without leaving what they're doing.
 */
export default function DeckPicker({
  label = 'Deck',
  title = 'Choose a deck',
  hint,
  value,
  onChange,
  decks,
  exclude,
  noneLabel,
  noneDescription,
  allowCreate = false,
  icon = 'decks',
  className,
  ...rest
}) {
  const [open, setOpen] = useState(false);
  const options = useMemo(() => {
    const ex = new Set(exclude ?? []);
    const list = flattenTree(decks ?? [])
      .filter((d) => !ex.has(d.id))
      .map((d) => ({ value: d.id, label: d.name, depth: d.depth }));
    return noneLabel ? [{ value: NONE, label: noneLabel, description: noneDescription }, ...list] : list;
  }, [decks, exclude, noneLabel, noneDescription]);

  const display = value ? deckPath(decks ?? [], value) : noneLabel;

  return (
    <>
      <PickerRow
        label={label}
        value={display}
        hint={hint}
        icon={icon}
        onClick={() => setOpen(true)}
        placeholder="Choose a deck"
        className={className}
        {...rest}
      />
      <PickerSheet
        open={open}
        onClose={() => setOpen(false)}
        title={title}
        options={options}
        value={value || (noneLabel ? NONE : undefined)}
        onChange={(v) => onChange(v === NONE ? null : v)}
        footer={
          allowCreate ? (
            <NewDeckInline
              onCreated={(id) => {
                onChange(id);
                setOpen(false);
              }}
            />
          ) : null
        }
      />
    </>
  );
}

function NewDeckInline({ onCreated }) {
  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);
  if (!adding) {
    return (
      <Button block icon="plus" onClick={() => setAdding(true)}>
        New deck
      </Button>
    );
  }
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const id = await createDeck(db, { name });
    onCreated(id);
  };
  return (
    <form className={styles.newDeck} onSubmit={submit}>
      <input
        autoFocus
        placeholder="New deck name"
        aria-label="New deck name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        enterKeyHint="done"
      />
      <Button type="submit" variant="primary" disabled={!name.trim()}>
        Create
      </Button>
    </form>
  );
}
